import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard, ClipboardList, Sparkles, FileText, XCircle, DollarSign,
  Users, BarChart3, Lightbulb, Shield, Settings, ChevronLeft, ChevronRight, Link2, CalendarClock
} from 'lucide-react';
import { Button } from '../ui/button';
import { useEnterprise } from '../../contexts/EnterpriseContext';

const WaffleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="2.5" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="7" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="2.5" cy="7" r="1.75" fill="#888888" />
    <circle cx="7" cy="7" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="7" r="1.75" fill="#888888" />
    <circle cx="2.5" cy="11.5" r="1.75" fill="#888888" />
    <circle cx="7" cy="11.5" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="11.5" r="1.75" fill="#888888" />
  </svg>
);

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/revq', icon: LayoutDashboard },
      { name: 'Schedule', href: '/revq/scheduler', icon: CalendarClock },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { name: 'AI Claims', href: '/revq/ai-claims', icon: Sparkles },
      { name: 'AI Recommendations', href: '/revq/ai-recommendations', icon: Lightbulb },
    ],
  },
  {
    label: 'Revenue Cycle',
    items: [
      { name: 'Encounters', href: '/revq/encounters', icon: ClipboardList },
      { name: 'Claims', href: '/revq/claims', icon: FileText },
      { name: 'Denials', href: '/revq/denials', icon: XCircle },
      { name: 'Payments & ERA', href: '/revq/payments', icon: DollarSign },
      { name: 'Patient Billing', href: '/revq/patient-billing', icon: Users },
    ],
  },
  {
    label: 'Insights',
    items: [
      { name: 'Analytics', href: '/revq/analytics', icon: BarChart3 },
      { name: 'Payers & Rules', href: '/revq/payers-rules', icon: Shield },
      { name: 'Integrations', href: '/revq/integrations', icon: Link2 },
    ],
  },
];

const RevQSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const enterprise = useEnterprise();
  const isEnterprise = enterprise?.isEnterprise;

  const enterpriseNav = isEnterprise ? [{
    label: 'Enterprise',
    items: [
      { name: 'Team', href: '/revq/team', icon: Users },
    ],
  }] : [];

  const allSections = [...NAV_SECTIONS, ...enterpriseNav];

  return (
    <aside className={cn(
      "fixed left-0 top-14 h-[calc(100vh-3.5rem)] bg-white border-r border-slate-200 transition-all duration-300 z-40 flex flex-col",
      collapsed ? "w-16" : "w-60"
    )} data-testid="revq-sidebar">

      {/* Waffle — All Apps */}
      <div className="px-3 pt-3 pb-1">
        <Link to="/landing"
          className="flex items-center gap-2.5 px-3 py-2.5 border border-slate-200 transition-all"
          style={{ borderRadius: 6 }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(107,92,231,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          data-testid="sidebar-all-apps"
        >
          <WaffleIcon />
          {!collapsed && (
            <div>
              <p style={{ fontSize: 12, fontWeight: 500, color: '#333333', lineHeight: '14px', margin: 0 }}>All apps</p>
              <p style={{ fontSize: 11, fontWeight: 400, color: '#888888', lineHeight: '13px', margin: 0 }}>Aria One platform</p>
            </div>
          )}
        </Link>
      </div>

      <nav className="flex-1 py-3 px-3 space-y-5 overflow-y-auto">
        {allSections.map((section) => (
          <div key={section.label}>
            {!collapsed && <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">{section.label}</p>}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                // Cross-app active check: when the user is on the canonical
                // `/lumina/scheduler` path (or its `/echo/scheduler` redirect
                // alias), Schedule entries in any app sidebar must appear
                // active — not just the sidebar of the hosting app.
                const SCHEDULER_PATHS = ['/lumina/scheduler', '/echo/scheduler', '/revq/scheduler'];
                const isSchedulerItem = SCHEDULER_PATHS.includes(item.href);
                const isOnScheduler = SCHEDULER_PATHS.includes(location.pathname);
                const isActive =
                  (isSchedulerItem && isOnScheduler) ||
                  location.pathname === item.href ||
                  (item.href !== '/revq' && location.pathname.startsWith(item.href));
                return (
                  <Link key={item.name} to={item.href} data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={cn("flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all",
                      isActive ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800",
                      collapsed && "justify-center px-2"
                    )}>
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    {!collapsed && <span>{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-slate-200 p-3">
        <Link to="/revq/settings" data-testid="nav-settings"
          className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-all", collapsed && "justify-center px-2")}>
          <Settings className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>
      </div>

      <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-8 h-6 w-6 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 shadow-sm">
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>
    </aside>
  );
};

export default RevQSidebar;
